import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Search, Plug } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/admin/AdminLayout";

function Integrations() {
  const [s, setS] = useState(null);
  const [params] = useSearchParams();
  useEffect(() => { api.get("/admin/sync/status").then((r) => setS(r.data)).catch(() => {}); if (params.get("shopify") === "connected") toast.success("Shopify connected"); }, [params]);
  const connect = async () => {
    try { const { data } = await api.get("/admin/shopify/install-url"); window.location.href = data.url; } catch (e) { toast.error(errorText(e)); }
  };
  if (!s) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="integrations-box">
      <div className="border border-[var(--line)] p-4 text-xs" data-testid="integration-shopify">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-sm">Shopify <span className="text-[var(--muted)] font-normal">{s.shopify.shop}</span></span>
          <span className={`tag ${s.shopify.connected ? "bg-green-700 text-white" : "tag-touring"}`} data-testid="shopify-status">{s.shopify.connected ? "connected" : "not connected"}</span>
        </div>
        <p className="text-[var(--muted)] mt-2">{s.shopify.connected ? `Installed ${new Date(s.shopify.installed_at).toLocaleString()} · scopes ${s.shopify.scope}` : "Install the app on the store to create single-use 25% codes."}</p>
        <p className="text-[var(--muted)] mt-1 break-all">Redirect URL: <span className="font-mono">{s.shopify.redirect_uri}</span></p>
        <button type="button" onClick={connect} className="btn-accent px-3 py-1.5 mt-3 flex items-center gap-2" data-testid="shopify-connect"><Plug size={13} /> {s.shopify.connected ? "Reconnect Shopify" : "Connect Shopify"}</button>
      </div>
      <div className="border border-[var(--line)] p-4 text-xs" data-testid="integration-mailchimp">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-sm">Mailchimp <span className="text-[var(--muted)] font-normal">audience {s.mailchimp.audience_web_id}</span></span>
          <span className={`tag ${s.mailchimp.list_id ? "bg-green-700 text-white" : "tag-touring"}`} data-testid="mailchimp-status">{s.mailchimp.list_id ? "connected" : "error"}</span>
        </div>
        <p className="text-[var(--muted)] mt-2">{s.mailchimp.list_id ? `List id ${s.mailchimp.list_id}. Merge fields are written first, tags after, bg-{slug} last.` : s.mailchimp.error}</p>
      </div>
    </div>
  );
}

async function downloadCsv() {
  const res = await api.get("/admin/signups.csv", { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = url; a.download = "banksy-guide-signups.csv"; a.click();
  URL.revokeObjectURL(url);
}

export default function SignupsAdmin() {
  const [data, setData] = useState({ count: 0, skipped_count: 0, items: [] });
  const [q, setQ] = useState("");
  const refresh = () => api.get("/admin/signups").then((r) => setData(r.data));
  useEffect(() => { refresh(); }, []);
  const purge = async () => {
    if (!window.confirm(`Delete all ${data.count} sign-up and consent records? This is irreversible (test-end cleanup).`)) return;
    try { const r = await api.delete("/admin/signups"); toast.success(`Deleted ${r.data.deleted} records`); refresh(); } catch (e) { toast.error(errorText(e)); }
  };
  const needle = q.trim().toLowerCase();
  const items = data.items.filter((v) => !needle || [v.code, v.email, v.first_name, v.last_name].some((x) => (x || "").toLowerCase().includes(needle)));
  const retry = async (v) => {
    try { const r = await api.post(`/admin/sync/${v.id}/retry`); const s = r.data.sync; const ok = ["ok", "n/a"].includes(s.shopify?.status) && s.mailchimp?.status === "ok"; toast[ok ? "success" : "error"](ok ? "Synced" : (s.shopify?.error || s.mailchimp?.error || "Still failing")); refresh(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const Sync = ({ v }) => {
    const failed = Object.values(v.sync).includes("failed");
    return (
      <span className="flex items-center gap-2 text-[0.65rem] uppercase tracking-wider" title={v.sync_error || ""} data-testid={`signup-sync-${v.id}`}>
        {["shopify", "mailchimp"].map((k) => v.sync[k] !== "n/a" && <span key={k} className={v.sync[k] === "ok" ? "text-green-500" : v.sync[k] === "failed" ? "text-accent" : "text-[var(--muted)]"}>{k}: {v.sync[k]}</span>)}
        {failed && <button type="button" onClick={() => retry(v)} className="border border-[var(--accent)] text-accent px-2 py-0.5 hover:bg-[var(--accent)] hover:text-white" data-testid={`signup-retry-${v.id}`}>Retry</button>}
      </span>
    );
  };

  return (
    <div data-testid="admin-signups-page">
      <PageTitle action={
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => downloadCsv().catch((e) => toast.error(errorText(e)))} className="border border-[var(--line)] px-4 py-2 text-xs flex items-center gap-2 hover:border-white transition-colors" data-testid="signups-csv"><Download size={14} /> Download CSV</button>
          <button type="button" onClick={purge} className="border border-[var(--accent)] text-accent px-4 py-2 text-xs hover:bg-[var(--accent)] hover:text-white transition-colors" data-testid="signups-purge">Delete all sign-up data</button>
        </div>}>Sign-ups <span className="text-[var(--muted)] text-xl" data-testid="signups-count">({data.count})</span></PageTitle>
      <Integrations />
      <p className="text-sm text-[var(--muted)] mb-2">Sign-ups are synced to Shopify (unique discount code, percentage per exhibition) and Mailchimp (audience "Artful DE"). Failed syncs retry automatically every 5 minutes (12 attempts, then admins are e-mailed); use Retry to force it.</p>
      <p className="text-xs text-[var(--muted)] mb-5" data-testid="signups-skipped">Visitors who continued without signing up (not stored as sign-ups): <span className="text-white">{data.skipped_count}</span></p>
      <label className="field flex items-center gap-3 max-w-sm mb-5 cursor-text"><Search size={16} className="text-[var(--muted)]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Check a voucher / discount code, name or e-mail" className="bg-transparent outline-none flex-1 text-sm" data-testid="signups-search" /></label>
      <div className="overflow-x-auto">
        <table className="admin-table text-sm min-w-full" data-testid="signups-table">
          <thead><tr><th>When</th><th>Exhibition</th><th>Name</th><th>E-mail</th><th>Country</th><th>Lang</th><th>Gift</th><th>Code</th><th>Consent</th><th>Sync</th></tr></thead>
          <tbody>{items.map((v) => (
            <tr key={v.id} data-testid={`signup-row-${v.id}`}>
              <td className="text-[var(--muted)] whitespace-nowrap">{new Date(v.created_at).toLocaleString()}</td>
              <td>{v.exhibition_slug}</td><td>{v.first_name} {v.last_name}</td><td>{v.email}</td><td>{v.country}</td><td className="uppercase text-xs">{v.language}</td>
              <td><span className={v.gift === "discount" ? "badge-accent" : v.gift === "poster" ? "tag tag-permanent" : "tag"}>{v.gift === "discount" ? `-${v.discount_percent || 25}%` : v.gift === "poster" ? "in-store gift" : "newsletter"}</span></td>
              <td className="font-mono text-xs" data-testid={`signup-code-${v.id}`}>{v.code}</td>
              <td className="text-xs text-[var(--muted)]">{v.consent?.policy_version} · {v.consent?.timestamp ? new Date(v.consent.timestamp).toLocaleDateString() : ""}</td>
              <td><Sync v={v} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
